import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { environment } from '../config/environment'
import i18n from '../i18n/config'
import { toastService } from './toast.service'

const NOT_AUTHENTICATED_DETAILS: ReadonlySet<string> = new Set([
  'Authentication credentials were not provided.',
  'Invalid token.',
  'Invalid token header. No credentials provided.',
])

class ApiService {
  private api: AxiosInstance
  private baseURL: string
  private authFailureRedirectScheduled = false

  constructor() {
    this.baseURL = environment.apiBaseUrl

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Use session cookies for authentication
    })

    // Request interceptor to add CSRF token for non-GET requests
    this.api.interceptors.request.use(
      (config) => {
        // Add CSRF token for non-GET requests
        if (config.method && config.method.toUpperCase() !== 'GET') {
          const csrfToken = this.getCsrfToken()
          if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken
          }
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status
        const data = error.response?.data
        const detail = typeof data?.detail === 'string' ? data.detail : ''
        const code = typeof data?.code === 'string' ? data.code : null
        const url = error.config?.url || ''

        const isNotAuthenticated =
          status === 401 ||
          (status === 403 && !code && NOT_AUTHENTICATED_DETAILS.has(detail))

        if (isNotAuthenticated) {
          if (!url.includes('logout/')) {
            this.handleAuthFailureRedirect('/login')
          }
          return Promise.reject(error)
        }

        if (status === 403) {
          if (code === 'session_replaced') {
            this.handleAuthFailureRedirect('/login?session=replaced')
            return Promise.reject(error)
          }

          const message =
            code && i18n.exists(code)
              ? i18n.t(code)
              : detail || 'You do not have permission to perform this action.'
          toastService.showError(message)
        }
        return Promise.reject(error)
      }
    )
  }

  private handleAuthFailureRedirect(target: string): void {
    if (this.authFailureRedirectScheduled) {
      return
    }
    this.authFailureRedirectScheduled = true

    void import('./feature-flags.service')
      .then(({ featureFlagsService }) => featureFlagsService.stopPolling())
      .catch(() => undefined)

    if (window.location.pathname.startsWith('/login')) {
      return
    }

    window.location.href = target
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config)
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config)
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config)
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config)
  }

  getApiUrl(path: string): string {
    return `${this.baseURL}${path}`
  }

  isAuthenticated(): boolean {
    return this.getCsrfToken() !== null
  }

  getCsrfToken(): string | null {
    const name = 'csrftoken'
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.startsWith(name + '=')) {
        return decodeURIComponent(cookie.substring(name.length + 1))
      }
    }
    return null
  }
}

export const apiService = new ApiService()
